import React, { useEffect, useRef, useState } from 'react';
import { Alert, Box, Divider } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { authService, AuthApiError } from '../../services/auth.service';
import config from '../../config/env.config';

// Minimal typing for the parts of Google Identity Services used here
declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (options: { client_id: string; callback: (response: { credential: string }) => void }) => void;
          renderButton: (parent: HTMLElement, options: Record<string, unknown>) => void;
        };
      };
    };
  }
}

const GIS_SCRIPT_SRC = 'https://accounts.google.com/gsi/client';

// Loads the Google Identity Services script once and shares the promise across mounts
let gisScriptPromise: Promise<void> | null = null;
const loadGisScript = (): Promise<void> => {
  if (window.google?.accounts?.id) return Promise.resolve();
  if (!gisScriptPromise) {
    gisScriptPromise = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = GIS_SCRIPT_SRC;
      script.async = true;
      script.onload = () => resolve();
      script.onerror = () => {
        gisScriptPromise = null;
        reject(new Error('Failed to load Google sign-in'));
      };
      document.head.appendChild(script);
    });
  }
  return gisScriptPromise;
};

interface GoogleSignInButtonProps {
  mode: 'signin' | 'signup';
}

/** "Sign in with Google" button; renders nothing when no Google client ID is configured. */
export const GoogleSignInButton = ({ mode }: GoogleSignInButtonProps) => {
  const { t, i18n } = useTranslation('auth');
  const buttonRef = useRef<HTMLDivElement>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!config.GOOGLE_CLIENT_ID) return;
    let cancelled = false;

    const handleCredential = async ({ credential }: { credential: string }) => {
      setError('');
      try {
        await authService.loginWithGoogle(credential);
        window.location.href = '/';
      } catch (err) {
        const code = (err as AuthApiError).code;
        setError(t(`google.errors.${code}`, { defaultValue: t('google.failed') }));
      }
    };

    loadGisScript()
      .then(() => {
        const container = buttonRef.current;
        if (cancelled || !container || !window.google) return;
        window.google.accounts.id.initialize({ client_id: config.GOOGLE_CLIENT_ID, callback: handleCredential });
        container.innerHTML = '';
        window.google.accounts.id.renderButton(container, {
          type: 'standard',
          theme: 'outline',
          size: 'large',
          shape: 'rectangular',
          text: mode === 'signup' ? 'signup_with' : 'signin_with',
          locale: i18n.language,
          // GIS caps the button at 400px
          width: Math.min(container.offsetWidth || 400, 400),
        });
      })
      .catch(() => !cancelled && setError(t('google.failed')));

    return () => {
      cancelled = true;
    };
  }, [mode, i18n.language, t]);

  if (!config.GOOGLE_CLIENT_ID) return null;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Divider sx={{ fontSize: 13, color: 'text.secondary' }}>{t('google.or')}</Divider>
      <Box ref={buttonRef} sx={{ display: 'flex', justifyContent: 'center', minHeight: 44 }} />
      {error && <Alert severity="error">{error}</Alert>}
    </Box>
  );
};
